import {RWSService} from '@rws-framework/client';

/**
 * Accessibility Service
 */
class AccessibilityService extends RWSService {
  static _IN_CLIENT: boolean = true;

  /**
   * Detect current view type based on URL pattern and DOM elements
   * 
   * - Instructor Dashboard: /:courseId/dashboard
   * - Student Dashboard: /:courseId/insights
   * - Student Gradebook: /:courseId/gradebook
   * - Course Home: /:courseId (single segment, no special keywords)
   * - Lesson: /:courseId/:lessonId (2 segments, both are valid ObjectIds)
   * - Page: /:courseId/:lessonId/:pageId[/:componentId] (3+ segments, all valid ObjectIds)
   */
  private detectCurrentView(): string | null {
    const hash = window.location.hash;
    const path = hash.replace('#', '').replace(/\?.*$/, ''); // Remove query params
    const segments = path.split('/').filter(s => s && s !== ''); // Clean empty segments
    const lastSegment = segments[segments.length - 1];
    // Exclude edit modes explicitly (they have /edit in URL)
    if (lastSegment === '/list' || lastSegment === '/edit') {
      return null;
    }
    
    if (lastSegment === 'dashboard') {
      return 'instructor-dashboard';
    }
    
    if (lastSegment === 'insights') {
      return 'student-dashboard';
    }
    
    if (lastSegment === 'gradebook') {
      return 'student-gradebook';
    }

    // Helper: Check if string looks like MongoDB ObjectId (24 hex chars)
    const isObjectId = (str: string): boolean => {
      return /^[0-9a-f]{24}$/i.test(str);
    };

    if (segments.length === 1 && isObjectId(segments[0])) {
      return 'course-home';
    }

    if (segments.length === 2) {
      const bothAreObjectIds = isObjectId(segments[0]) && isObjectId(segments[1]);
      
      if (bothAreObjectIds) {
        return 'lesson';
      }
    }
    
    if (segments.length >= 3) {
      const allAreObjectIds = segments.every(seg => isObjectId(seg));
      
      if (allAreObjectIds) {
        return 'page';
      }
    }

    return null;
  }

  shouldShowSkipLink(): boolean {
    const currentView = this.detectCurrentView();
    
    const allowedViews = [
      'instructor-dashboard',
      'student-dashboard',
      'student-gradebook',
      'course-home',
      'lesson',
      'page'
    ];
    
    const shouldShow = currentView ? allowedViews.includes(currentView) : false;    
    return shouldShow;
  }

  findMainContentAnchor(): HTMLElement | null {
    const targetElement = document.querySelector('[data-main-content-target]') as HTMLElement;
    
    if (targetElement) {
      return targetElement;
    }

    // Check shadow roots (e.g., gradebook-student)
    if (!targetElement) {
      const gradebook = document.querySelector('gradebook-student')
      const gradebookMainContent = (gradebook as any).shadowRoot.querySelector('[data-main-content-target]');
      return gradebookMainContent;
    }
  }

  /**
   * Skip to main content functionality
   * Sets focus on the main content area and announces to screen readers
   */
  skipToMainContent(): void {
    const mainContent = this.findMainContentAnchor();
    
    if (!mainContent) {
      return;
    }

    if (!mainContent.hasAttribute('tabindex')) {
      mainContent.setAttribute('tabindex', '-1');
    }

    // Scroll into view first (without smooth to avoid race condition)
    mainContent.scrollIntoView({ 
      behavior: 'auto', 
      block: 'start' 
    });
    
    // Small delay to ensure scroll completes before focusing
    setTimeout(() => {
      mainContent.focus();
    }, 100);
  }
}

export default AccessibilityService.getSingleton();

export {AccessibilityService as AccessibilityServiceInstance}